package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.market.payroll.MonthPayFactors
import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.market.payroll.PayrollRules
import com.evoapps.evoshift.domain.market.payroll.PayslipInput
import com.evoapps.evoshift.domain.market.payroll.WorkplacePayrollSettings
import com.evoapps.evoshift.domain.model.Adjustment
import com.evoapps.evoshift.domain.model.AdjustmentType
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.ShiftFinancials
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.model.paidSpan
import com.evoapps.evoshift.domain.model.BreakPolicy
import com.evoapps.evoshift.domain.repository.AbsenceRepository
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.usecase.finance.GetOwnPayFactsUseCase
import com.evoapps.evoshift.domain.usecase.finance.OwnMonthPayEstimate
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flow
import kotlin.math.round
import com.evoapps.evoshift.domain.util.AppClock.now
import com.evoapps.evoshift.domain.util.calculateDurationHours
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

data class EmployeeDashboardState(
    /** The roster month the figures below are for (not necessarily this month), e.g. 2027 and 3. */
    val year: Int,
    val month: Int,
    val nextShift: Shift?,
    /** Gross for the roster month: clocked pay where clocked, else planned (or the salary), plus bonuses. */
    val monthlyEstimatedEarnings: Double,
    /**
     * What reaches the account: the take-home pay of that gross under the market's rules, minus
     * deductions, plus expense refunds; null without readable pay rates.
     */
    val monthlyEstimatedNet: Double? = null,
    /** The person's own bonuses, deductions and refunds of the month (SH-78), by date. */
    val adjustments: List<Adjustment> = emptyList(),
    /** False for an owner whose shifts are unpaid at this workplace: there are no earnings to show. */
    val isPaid: Boolean = true,
    /**
     * SH-57: overtime, Sunday and holiday work, the minimum-wage top-up, sick pay, holiday pay and
     * pay terms changed during the month, already in [monthlyEstimatedEarnings] (PPK in the net);
     * null without readable pay terms.
     */
    val payFactors: MonthPayFactors? = null,
    val plannedHours: Double,
    val marketplaceCount: Int,
    val unreadNotifications: Int
)

class GetEmployeeDashboardUseCase(
    private val shiftRepository: ShiftRepository,
    private val financialRepository: FinancialRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository,
    /** SH-57: the person's absences for sick pay and holiday pay; without it the estimate has none. */
    private val absenceRepository: AbsenceRepository? = null,
    /** SH-223: the person's overtime and work events (paid work outside the shifts). */
    private val workRequestRepository: com.evoapps.evoshift.domain.repository.WorkRequestRepository? = null
) {
    private val ownPayFacts = GetOwnPayFactsUseCase(shiftRepository, financialRepository, userRepository, workplaceRepository, absenceRepository, workRequestRepository)

    fun invoke(userId: String, rosterId: String): Flow<EmployeeDashboardState> {
        val myShiftsFlow = shiftRepository.observeShiftsForUser(userId, "2020-01-01")
        val pendingSwapsFlow = shiftRepository.observePendingSwaps(rosterId)
        val unreadCountFlow = userRepository.observeUnreadNotificationsCount(userId)

        val (rosterYear, rosterMonth) = RosterIds.yearMonthOf(rosterId)
        // SH-165: the same facts and estimate as a swap's pay change (OwnMonthPayEstimate).
        val financialsFlow = flow { emit(ownPayFacts.load(userId, rosterId)) }

        return combine(
            myShiftsFlow,
            pendingSwapsFlow,
            financialsFlow,
            unreadCountFlow
        ) { allMyShifts, swaps, facts, unreadCount ->
            // Planned times are local ISO date-times: compare with local now, not a UTC instant.
            val nowLocal = now().toLocalDateTime(TimeZone.currentSystemDefault()).toString()
            // A roster being drafted is the manager's: the person sees published shifts only
            // (as the schedule, the clock and the widget do).
            val myShifts = allMyShifts.filter { it.isPublished }

            // 1. Next Shift
            val nextShift = myShifts
                .filter { it.plannedTime.start > nowLocal }
                .minByOrNull { it.plannedTime.start }

            // 2. Filter shifts for the selected roster
            val rosterShifts = myShifts.filter { it.rosterId == rosterId }

            // 3. Stats calculation
            val totalHours = rosterShifts.sumOf { calculateDurationHours(it.plannedTime.start, it.plannedTime.end) }

            val pay = OwnMonthPayEstimate.of(userId, rosterId, myShifts, facts)

            // 4. Marketplace count
            val marketCount = swaps.count {
                it.userId != userId && it.swapRequest?.status == SwapStatus.AVAILABLE 
            }

            EmployeeDashboardState(
                year = rosterYear,
                month = rosterMonth,
                nextShift = nextShift,
                monthlyEstimatedEarnings = pay.gross,
                monthlyEstimatedNet = pay.net,
                adjustments = facts.adjustments,
                isPaid = facts.isPaid,
                payFactors = pay.factors,
                plannedHours = round(totalHours * 100) / 100.0,
                marketplaceCount = marketCount,
                unreadNotifications = unreadCount
            )
        }
    }
}
